import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { login, register, forgotPassword, resetPassword } from '@app/api/auth.api';
import { LoginDto, RegisterDto, ForgotPasswordDto, ResetPasswordDto, UserResponseDto } from '@app/types/api.types';
import { setUser } from '@app/store/slices/userSlice';
import { deleteToken, deleteUser, persistToken, readToken, persistUser } from '@app/services/localStorage.service';

export interface AuthSlice {
  token: string | null;
  isAuthenticated: boolean;
  user: UserResponseDto | null;
  loading: boolean;
  error: string | null;
}

const initialState: AuthSlice = {
  token: readToken(),
  isAuthenticated: !!readToken(),
  user: null,
  loading: false,
  error: null,
};

export const doLogin = createAsyncThunk(
  'auth/doLogin',
  async (loginPayload: LoginDto, { dispatch, rejectWithValue }) => {
    try {
      const res = await login(loginPayload);
      persistToken(res.access_token);
      persistUser(res.user);
      dispatch(setUser(res.user));
      return { token: res.access_token, user: res.user };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

export const doRegister = createAsyncThunk(
  'auth/doRegister',
  async (registerPayload: RegisterDto, { rejectWithValue }) => {
    try {
      return await register(registerPayload);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Registration failed');
    }
  }
);

export const doForgotPassword = createAsyncThunk(
  'auth/doForgotPassword',
  async (forgotPasswordPayload: ForgotPasswordDto, { rejectWithValue }) => {
    try {
      await forgotPassword(forgotPasswordPayload);
      return true;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to send reset email');
    }
  }
);

export const doResetPassword = createAsyncThunk(
  'auth/doResetPassword',
  async (resetPasswordPayload: ResetPasswordDto, { rejectWithValue }) => {
    try {
      await resetPassword(resetPasswordPayload);
      return true;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Password reset failed');
    }
  }
);

export const doLogout = createAsyncThunk('auth/doLogout', (_, { dispatch }) => {
  deleteToken();
  deleteUser();
  dispatch(setUser(null));
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Login
    builder.addCase(doLogin.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(doLogin.fulfilled, (state, action) => {
      state.loading = false;
      state.token = action.payload.token;
      state.user = action.payload.user;
      state.isAuthenticated = true;
    });
    builder.addCase(doLogin.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Register
    builder.addCase(doRegister.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(doRegister.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(doRegister.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Forgot Password
    builder.addCase(doForgotPassword.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(doForgotPassword.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(doForgotPassword.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Reset Password
    builder.addCase(doResetPassword.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(doResetPassword.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(doResetPassword.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Logout
    builder.addCase(doLogout.fulfilled, (state) => {
      state.token = null;
      state.user = null;
      state.isAuthenticated = false;
    });
  },
});

export const { clearError } = authSlice.actions;
export default authSlice.reducer;
